import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, formatINR } from "@/lib/api";
import { CheckCircle2, Clock, Stethoscope, TrendingUp, Video } from "lucide-react";

export default function PractitionerDashboard() {
  const nav = useNavigate();
  const [prac, setPrac] = useState(null);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const p = await http.get("/practitioners/me");
        setPrac(p.data);
        const b = await http.get("/consultations/mine");
        setBookings(b.data);
      } catch (err) {
        if (err?.response?.status === 404) { nav("/practitioner/join", { replace: true }); return; }
        console.error("Practitioner dashboard load failed", err);
      }
    })();
  }, [nav]);

  if (!prac) {
    return (
      <div className="max-w-xl mx-auto p-10 text-center" data-testid="practitioner-loading">
        <p className="text-sm text-[#5A6B5D]">Loading your practitioner portal…</p>
      </div>
    );
  }

  const earnings = bookings.filter((b) => b.payment_status === "paid").reduce((s, b) => s + (b.practitioner_earning || 0), 0);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="practitioner-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono text-[#D97706] uppercase tracking-wider">Practitioner Portal</div>
          <h1 className="font-serif text-4xl font-bold text-[#1A201C] mt-1">{prac.display_title} {prac.full_name}</h1>
          <div className="mt-2 flex items-center gap-2 text-xs text-[#5A6B5D]">
            <StatusBadge status={prac.status} />
            <span className="font-mono">BAMS · {prac.bams_reg_no}</span>
          </div>
        </div>
      </div>

      {prac.status === "pending" && (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-2xl p-5 text-sm">
          <div className="flex items-center gap-2 text-amber-900 font-semibold"><Clock className="w-4 h-4" /> Application under review</div>
          <p className="text-amber-800 mt-1">Amiroop Healthion is verifying your BAMS and council credentials. Typical turnaround: 48 hours.</p>
        </div>
      )}

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Metric label="Your Fee" value={formatINR(prac.consultation_fee)} icon={TrendingUp} />
        <Metric label="Consultations Done" value={prac.total_consultations || 0} icon={Stethoscope} />
        <Metric label="Net Earnings" value={formatINR(earnings)} icon={CheckCircle2} />
        <Metric label="Amiroop Commission" value={`${prac.platform_commission_rate}%`} icon={TrendingUp} />
      </div>

      <h2 className="mt-10 font-serif text-2xl font-bold text-[#1A201C]">Upcoming & Past Consultations</h2>
      <div className="mt-4 space-y-3">
        {bookings.length === 0 ? (
          <p className="text-sm text-[#5A6B5D] py-8 text-center bg-white border border-[#E2DDD5] rounded-2xl">No consultations booked yet.</p>
        ) : bookings.map((b) => (
          <div key={b.id} className="bg-white border border-[#E2DDD5] rounded-2xl p-4" data-testid={`prac-booking-${b.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs font-mono text-[#5A6B5D]">#{b.id.slice(0, 8).toUpperCase()} · {b.mode.replace(/_/g, " ").toUpperCase()}</div>
                <div className="font-semibold text-sm mt-1">{b.user_name} ({b.user_email})</div>
                <div className="text-xs text-[#5A6B5D] mt-1">📅 {b.slot_date} · 🕐 {b.slot_time}</div>
                <div className="mt-2 text-xs text-[#1A201C] italic bg-[#FAF8F5] border border-[#E2DDD5] rounded-lg p-2 max-w-xl">"{b.concern}"</div>
              </div>
              <div className="text-right">
                <div className="font-bold text-[#1E3A2B]">{formatINR(b.practitioner_earning)}</div>
                <div className="text-[10px] font-mono text-[#5A6B5D]">You earn (net)</div>
                {b.mode === "video" && b.meeting_link && (
                  <a href={b.meeting_link} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs bg-[#1E3A2B] text-white px-3 py-1.5 rounded-full">
                    <Video className="w-3 h-3" /> Join
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Metric({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-[#E2DDD5] rounded-2xl p-4">
      <Icon className="w-4 h-4 text-[#D97706]" />
      <div className="text-xs font-mono uppercase text-[#5A6B5D] mt-2">{label}</div>
      <div className="font-serif text-2xl font-bold text-[#1A201C] mt-1">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    rejected: "bg-red-50 text-red-800 border-red-200",
  };
  return <span className={`inline-block text-[10px] uppercase font-mono px-2 py-0.5 rounded-full border ${map[status] || "bg-gray-50 border-gray-200"}`}>{status}</span>;
}
